import { MealType, ScanOutcomeCode } from '../../services/api';
import { toPersianNumber } from '../../utils/persianNumber';

// Dark, high-contrast palette for the restaurant delivery screen. Hues follow the admin
// theme's navy/indigo brand; text colours are chosen to stay above WCAG AA on `surface`.
export const M = {
    bg: '#0b1024',
    surface: '#141b36',
    surfaceRaised: '#1b2446',
    surfaceHover: '#222c55',
    border: 'rgba(139, 170, 255, 0.14)',
    borderStrong: 'rgba(139, 170, 255, 0.30)',

    text: '#f5f7ff',
    textMuted: '#b3bddd',
    textFaint: '#8a95ba',

    primary: '#5b7cfa',
    primaryStrong: '#4a6ce8',
    primaryText: '#9db4ff',
    primarySoft: 'rgba(91, 124, 250, 0.16)',

    success: '#22c55e',
    successStrong: '#15803d',
    successText: '#4ade80',
    successSoft: 'rgba(34, 197, 94, 0.14)',

    warning: '#f59e0b',
    warningText: '#fbbf24',
    warningSoft: 'rgba(245, 158, 11, 0.14)',

    danger: '#ef4444',
    dangerText: '#f87171',
    dangerSoft: 'rgba(239, 68, 68, 0.14)',

    info: '#38bdf8',
    infoText: '#7dd3fc',
    infoSoft: 'rgba(56, 189, 248, 0.14)',

    font: 'Vazir, sans-serif',
    radiusControl: '12px',
    radiusCard: '16px',
    cardShadow: '0 4px 12px rgba(0,0,0,0.05)',
    controlHeight: '48px',
} as const;

export type Tone = 'success' | 'warning' | 'danger' | 'info' | 'primary';

export const toneColors = (tone: Tone) => ({
    main: M[tone],
    text: M[`${tone}Text` as const],
    soft: M[`${tone}Soft` as const],
});

export const MEAL_LABELS: Record<MealType, string> = {
    breakfast: 'صبحانه',
    lunch: 'ناهار',
    dinner: 'شام',
};

export const MEAL_ORDER: MealType[] = ['breakfast', 'lunch', 'dinner'];

// Same windows the backend uses when a scan arrives without a meal type
export const mealTypeFromClock = (date = new Date()): MealType => {
    const hour = date.getHours();
    if (hour >= 16) return 'dinner';
    if (hour <= 10) return 'breakfast';
    return 'lunch';
};

// Local calendar date; toISOString() would give yesterday before 03:30 in Tehran
export const localYmd = (date = new Date()) =>
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

export const formatClock = (value: string | Date | null | undefined, withSeconds = false) => {
    if (!value) return '';
    const d = typeof value === 'string' ? new Date(value) : value;
    if (Number.isNaN(d.getTime())) return '';
    const pad = (n: number) => String(n).padStart(2, '0');
    const text = `${pad(d.getHours())}:${pad(d.getMinutes())}${withSeconds ? `:${pad(d.getSeconds())}` : ''}`;
    return toPersianNumber(text);
};

// Match Persian and Arabic letter forms, ZWNJ and digits alike when filtering on the client
export const normalizeFa = (s: string) =>
    s
        .replace(/ي/g, 'ی')
        .replace(/ك/g, 'ک')
        .replace(/‌/g, ' ')
        .replace(/[۰-۹]/g, (d) => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(d)))
        .replace(/[٠-٩]/g, (d) => String('٠١٢٣٤٥٦٧٨٩'.indexOf(d)))
        .replace(/\s+/g, ' ')
        .toLowerCase()
        .trim();

export const initials = (firstName?: string | null, lastName?: string | null) =>
    `${(firstName || '').trim().charAt(0)}${(lastName || '').trim().charAt(0)}` || '؟';

// How the delivery screen presents each scan outcome: colour, headline and sound
export type ScanKind = 'delivered' | 'already' | 'noReservation' | 'invalidToken' | 'notFound' | 'unknownCard' | 'error' | 'undone';

export const SCAN_KIND: Record<ScanKind, { tone: Tone; title: string; dismissMs: number | null }> = {
    delivered: { tone: 'success', title: 'تحویل داده شد', dismissMs: 6000 },
    already: { tone: 'warning', title: 'قبلاً تحویل گرفته است', dismissMs: 9000 },
    noReservation: { tone: 'danger', title: 'برای این وعده رزرو ندارد', dismissMs: 9000 },
    invalidToken: { tone: 'danger', title: 'ژتون پذیرفته نشد', dismissMs: 9000 },
    notFound: { tone: 'danger', title: 'شناسایی نشد', dismissMs: 8000 },
    unknownCard: { tone: 'info', title: 'کارت ناشناس', dismissMs: null },
    error: { tone: 'danger', title: 'خطا در ارتباط با سرور', dismissMs: 8000 },
    undone: { tone: 'info', title: 'تحویل لغو شد', dismissMs: 6000 },
};

export const kindFromCode = (code: ScanOutcomeCode | undefined, fallbackSuccess: boolean): ScanKind => {
    switch (code) {
        case 'DELIVERED':
        case 'GROUP_DELIVERED':
        case 'TOKEN_DELIVERED':
            return 'delivered';
        case 'ALREADY_DELIVERED':
        case 'GROUP_ALREADY_DELIVERED':
            return 'already';
        case 'NO_RESERVATION':
        case 'GROUP_NO_RESERVATION':
            return 'noReservation';
        case 'TOKEN_NOT_FOUND':
        case 'TOKEN_CANCELLED':
        case 'TOKEN_WRONG_DAY':
        case 'TOKEN_WRONG_MEAL':
            return 'invalidToken';
        case 'NOT_FOUND':
        case 'GROUP_INACTIVE':
        case 'EMPTY':
            return 'notFound';
        default:
            return fallbackSuccess ? 'delivered' : 'notFound';
    }
};
